<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, AtomButton, AtomRadio } from "@chatfood/design-system";
import { ref, onMounted, computed } from "vue";
import { AddressBook } from "../address-book";
import { SchedulePicker } from "../schedule-picker";
import { BasketEventName } from "../basket-event-name";
import { BasketPax } from "../basket-pax";
import type { IDeliveryOrderingDetailsEmits } from "./emits";
import type { IDomainCustomerCoveredAddress } from "~/domain/customer-covered-address";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useBusinessStore } from "~/stores/business";
import { useDelivery } from "~/composables/use-delivery";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import { useBugReporter, useDirectBackend } from "#imports";

const emits = defineEmits<IDeliveryOrderingDetailsEmits>();

const css = bemBuilder("delivery-ordering-details");
const { t } = useLocale();

const { onDeliverTo } = useDelivery();
const { resolvePath } = useNavigation();
const { report } = useBugReporter();

const basket = useBasketStore();
const customer = useCustomerStore();
const business = useBusinessStore();

type IOrderType = "asap" | "scheduled";

const orderType = ref<IOrderType>(basket.scheduledTime ? "scheduled" : "asap");

const scheduledTime = ref<IDomainOrderScheduledTime | undefined>(
  basket.orderingMode === "delivery" ? basket.scheduledTime : undefined,
);

const selectedAddressId = ref<string | undefined>(basket.addressId);

const isSettingMode = ref(false);

const selectedOutletId = ref<string>(basket.outletId);

const acceptsAsapOrders = ref(false);
const acceptsPreOrders = ref(false);
const estimatedCompletionMinutes = ref(0);

const { outletRepo } = await useDirectBackend();

const loadOrderingModeSettings = async (outletId: string) => {
  try {
    const [settings] = await outletRepo.getSettings({
      outletId,
      salesChannel: basket.salesChannel,
      orderingMode: ["delivery"],
    });

    if (!settings) return;

    acceptsAsapOrders.value = settings.acceptsAsapOrders;
    acceptsPreOrders.value = settings.acceptsPreOrders;
    estimatedCompletionMinutes.value = settings.estimatedCompletionMinutes;
  } catch (e) {
    report(e);
  }
};

const selectedAddress = computed<IDomainCustomerCoveredAddress | undefined>(
  () => {
    const address = customer.addresses.find(
      (address) => address.id === selectedAddressId.value,
    );

    if (!address?.canDeliver) return;

    return address;
  },
);

const onSelectAddress = async (address: IDomainCustomerCoveredAddress) => {
  await loadOrderingModeSettings(address.deliveryCoverage.outletId);

  selectedOutletId.value = address.deliveryCoverage.outletId;
  selectedAddressId.value = address.id;

  scheduledTime.value = undefined;
  orderType.value = acceptsAsapOrders.value ? "asap" : "scheduled";
};

const onConfirm = async () => {
  isSettingMode.value = true;

  try {
    if (
      selectedAddressId.value &&
      selectedAddressId.value !== basket.addressId
    ) {
      if (!selectedAddress.value) {
        isSettingMode.value = false;

        throw new Error(
          "[DeliveryOrderingDetails] Selected address id is not valid.",
        );
      }

      await onDeliverTo(selectedAddress.value);
    }

    if (business.isCatering) {
      await basket.setMeta({
        eventName: eventName.value ?? null,
        pax: typeof pax.value === "number" ? pax.value : null,
      });
    }

    await basket.setScheduledTime(scheduledTime.value);

    emits("update");
  } finally {
    isSettingMode.value = false;
  }
};

const onSetSchedule = (value?: IDomainOrderScheduledTime) => {
  scheduledTime.value = value;
};

const isButtonDisabled = computed<boolean>(() => {
  return (
    basket.currentLocation === undefined &&
    selectedAddressId.value === undefined
  );
});

onMounted(() => {
  loadOrderingModeSettings(selectedOutletId.value);
});

const eventName = ref(basket.eventName);
const pax = ref(basket.pax);
</script>

<template>
  <div :class="css('')">
    <div :class="css('mode-config')">
      <AddressBook
        :active-address-id="selectedAddressId"
        :hide-deliver-cta="true"
        @selected="onSelectAddress"
      />
      <NuxtLink
        :to="
          resolvePath('order', {
            orderingMode: 'delivery',
          })
        "
      >
        <AtomText type="link" color="color-primary">
          {{ t("components.delivery_ordering_details.change_outlet_cta") }}
        </AtomText>
      </NuxtLink>
    </div>
    <div v-if="business.isCatering" :class="css('meta-fields')">
      <AtomText type="subtitle">
        {{ t("components.delivery_ordering_details.details") }}
      </AtomText>
      <BasketEventName @update="(value) => (eventName = value)" />
      <BasketPax @update="(value) => (pax = value)" />
    </div>
    <div v-if="acceptsAsapOrders || acceptsPreOrders" :class="css('scheduler')">
      <AtomText type="subtitle">
        {{ t("components.delivery_ordering_details.schedule_title") }}
      </AtomText>
      <AtomRadio
        v-if="acceptsAsapOrders"
        name="deliveryScheduleOrder"
        :checked="orderType === 'asap'"
        value="asap"
        @change="() => (orderType = 'asap')"
      >
        {{
          t("components.delivery_ordering_details.asap_radio", {
            from: estimatedCompletionMinutes,
            to: estimatedCompletionMinutes + ETA_PREPARATION_MARGIN,
          })
        }}
      </AtomRadio>
      <AtomRadio
        v-if="acceptsPreOrders"
        name="deliveryScheduleOrder"
        :checked="orderType === 'scheduled'"
        value="scheduled"
        @change="() => (orderType = 'scheduled')"
      >
        {{ t("components.delivery_ordering_details.schedule_radio") }}
      </AtomRadio>
      <SchedulePicker
        v-if="acceptsPreOrders && orderType === 'scheduled' && selectedOutletId"
        :is-root="true"
        :show-label="false"
        ordering-mode="delivery"
        :outlet-id="selectedOutletId"
        picker-align="row"
        :value="scheduledTime"
        @select="onSetSchedule"
        @vue:unmounted="() => onSetSchedule()"
      />
    </div>
    <div :class="css('footer')">
      <AtomButton
        size="large"
        :is-loading="isSettingMode"
        wide
        :is-disabled="isButtonDisabled"
        @click="onConfirm"
      >
        {{ t("components.delivery_ordering_details.main_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.delivery-ordering-details {
  min-width: 375px;

  &__mode-config {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    overflow: auto;
    max-height: 350px;

    @media (width < $breakpoint-lg) {
      max-height: 250px;
    }
  }

  &__meta-fields {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-lg);
  }

  &__outlet-card {
    cursor: default;
  }

  &__scheduler {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  &__footer {
    margin: var(--spacing-lg) var(--spacing-md);
  }
}
</style>
